import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import { getApiBaseUrl } from '../../config/apiBase';

// PROMPT-134: Host-Based API Calls (No localhost)
const API_BASE_URL = getApiBaseUrl();

const TestSMS = () => {
  const [loading, setLoading] = useState(false);
  const [connectors, setConnectors] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [entities, setEntities] = useState([]);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Fields
    mobile: '',
    message: '',
    sender_id: '',
    
    // Selection Fields
    connector_id: '',
    route_id: '',
    entity_id: '',
    header_id: '',
    template_id: '',
    
    // Message Type
    message_type: 'PROMOTIONAL',
    
    // SMS Options
    flash_sms: false,
    unicode: false,
    long_sms: true,
    
    // DLT Options
    dlt_required: true,
    dlt_entity_id: '',
    dlt_template_id: '',
    
    // Priority & Scheduling
    priority: 'NORMAL',
    schedule_time: '',
    
    // Advanced Options
    validity_period: 2880, // 48 hours in minutes
    delivery_report: true,
    custom_params: '',
  });

  useEffect(() => {
    fetchConnectors();
    fetchRoutes();
    fetchTemplates();
    fetchHeaders();
    fetchEntities();
  }, []);

  const fetchConnectors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/smpp-connectors');
      setConnectors(response.data.connectors || []);
    } catch (error) {
      console.error('Error fetching connectors:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/routes');
      setRoutes(response.data.routes || []);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/templates');
      // Handle paginated response structure
      setTemplates(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]); // Ensure templates is always an array
    }
  };

  const fetchHeaders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/headers');
      // Handle paginated response structure
      setHeaders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching headers:', error);
      setHeaders([]); // Ensure headers is always an array
    }
  };

  const fetchEntities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/admin/entities');
      // Handle paginated response structure
      setEntities(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching entities:', error);
      setEntities([]); // Ensure entities is always an array
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTemplateSelect = (templateId) => {
    setFormData(prev => ({ ...prev, template_id: templateId }));
    
    if (templateId) {
      const template = templates.find(t => t.id.toString() === templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          message: template.template_content || '',
          dlt_template_id: template.dlt_template_id || '',
        }));
      }
    }
  };

  const handleHeaderSelect = (headerId) => {
    setFormData(prev => ({ ...prev, header_id: headerId }));
    
    if (headerId) {
      const header = headers.find(h => h.id.toString() === headerId);
      if (header) {
        setFormData(prev => ({
          ...prev,
          sender_id: header.header_name || '',
          entity_id: header.entity_id || '',
        }));
      }
    }
  };

  const handleEntitySelect = (entityId) => {
    setFormData(prev => ({ ...prev, entity_id: entityId }));
    
    if (entityId) {
      const entity = entities.find(e => e.id.toString() === entityId);
      if (entity) {
        setFormData(prev => ({
          ...prev,
          dlt_entity_id: entity.entity_id || '',
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.mobile || !formData.message) {
      alert('Mobile number and message are required');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      
      // Build request payload
      const payload = {
        mobile: formData.mobile,
        message: formData.message,
        sender_id: formData.sender_id || '',
        message_type: formData.message_type || 'PROMOTIONAL',
        flash_sms: formData.flash_sms || false,
        unicode: formData.unicode || false,
        long_sms: formData.long_sms || false,
        dlt_required: formData.dlt_required || false,
        dlt_entity_id: formData.dlt_entity_id || '',
        dlt_template_id: formData.dlt_template_id || '',
        priority: formData.priority || 'NORMAL',
        plan_id: 0,
        cost: 0,
      };
      
      console.log('Sending payload:', payload);
      
      // Add optional integer fields only if they have values
      if (formData.connector_id) {
        payload.connector_id = parseInt(formData.connector_id);
      }
      if (formData.route_id) {
        payload.route_id = parseInt(formData.route_id);
      }
      if (formData.entity_id) {
        payload.entity_id = parseInt(formData.entity_id);
      }
      if (formData.header_id) {
        payload.header_id = parseInt(formData.header_id);
      }
      if (formData.template_id) {
        payload.template_id = parseInt(formData.template_id);
      }
      
      const response = await api.post('/admin/test/send-sms', payload
      );

      setResult({
        success: true,
        data: response.data,
        message: 'SMS sent successfully!'
      });
    } catch (error) {
      console.error('Full error:', error);
      console.error('Error response:', error.response?.data);
      setResult({
        success: false,
        error: error.response?.data?.error || error.message,
        details: error.response?.data?.details || '',
        debug: error.response?.data?.debug || null,
        message: 'Failed to send SMS'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      mobile: '',
      message: '',
      sender_id: '',
      connector_id: '',
      route_id: '',
      entity_id: '',
      header_id: '',
      template_id: '',
      message_type: 'PROMOTIONAL',
      flash_sms: false,
      unicode: false,
      long_sms: true,
      dlt_required: true,
      dlt_entity_id: '',
      dlt_template_id: '',
      priority: 'NORMAL',
      schedule_time: '',
      validity_period: 2880,
      delivery_report: true,
      custom_params: '',
    });
    setResult(null);
  };

  const getMessageLength = () => {
    if (!formData.message) return 0;
    return formData.message.length;
  };

  const getMessageParts = () => {
    const length = getMessageLength();
    if (length === 0) return 0;
    
    if (formData.unicode) {
      return Math.ceil(length / 70);
    } else {
      return Math.ceil(length / 160);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800">📱 Test SMS</h1>
            <button
              type="button"
              onClick={handleReset}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
            >
              🔄 Reset
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">📋 Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number *
                  </label>
                  <input
                    type="text"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    placeholder="919876543210"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter mobile number with country code</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sender ID
                  </label>
                  <input
                    type="text"
                    name="sender_id"
                    value={formData.sender_id}
                    onChange={handleInputChange}
                    placeholder="SMSINF"
                    maxLength="6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message * ({getMessageLength()} chars, {getMessageParts()} part{getMessageParts() !== 1 ? 's' : ''})
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Enter your message here..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>

            {/* Quick Selection from DLT */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-green-800 mb-4">🎯 Quick Selection (DLT)</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Entity
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: '-- Select Entity --' },
                      ...(Array.isArray(entities) ? entities.map(e => ({
                        value: e.id.toString(),
                        label: e.entity_name
                      })) : [])
                    ]}
                    value={formData.entity_id}
                    onChange={handleEntitySelect}
                    placeholder="Select Entity"
                    clearable={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Header
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: '-- Select Header --' },
                      ...(Array.isArray(headers) ? headers.map(h => ({
                        value: h.id.toString(),
                        label: `${h.header_name} - ${h.header_type}`
                      })) : [])
                    ]}
                    value={formData.header_id}
                    onChange={handleHeaderSelect}
                    placeholder="Select Header"
                    clearable={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Template
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: '-- Select Template --' },
                      ...(Array.isArray(templates) ? templates.map(t => ({
                        value: t.id.toString(),
                        label: t.template_name
                      })) : [])
                    ]}
                    value={formData.template_id}
                    onChange={handleTemplateSelect}
                    placeholder="Select Template"
                    clearable={true}
                  />
                </div>
              </div>
            </div>

            {/* Routing Options */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-purple-800 mb-4">🛣️ Routing Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Connector (Optional)
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: '-- Select Connector --' },
                      ...(Array.isArray(connectors) ? connectors.filter(c => c.is_active).map(c => ({
                        value: c.id.toString(),
                        label: `${c.name} (${c.provider})`
                      })) : [])
                    ]}
                    value={formData.connector_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, connector_id: value }))}
                    placeholder="Auto Select Connector"
                    clearable={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Route (Optional)
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: '', label: '-- Select Route --' },
                      ...(Array.isArray(routes) ? routes.filter(r => r.is_active).map(r => ({
                        value: r.id.toString(),
                        label: `${r.name} - ${r.route_type}`
                      })) : [])
                    ]}
                    value={formData.route_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, route_id: value }))}
                    placeholder="Auto Select Route"
                    clearable={true}
                  />
                </div>
              </div>
            </div>

            {/* Message Options */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-yellow-800 mb-4">⚙️ Message Options</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Type
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: 'PROMOTIONAL', label: 'Promotional' },
                      { value: 'TRANSACTIONAL', label: 'Transactional' },
                      { value: 'OTP', label: 'OTP' },
                      { value: 'SERVICE', label: 'Service' }
                    ]}
                    value={formData.message_type}
                    onChange={(value) => setFormData(prev => ({ ...prev, message_type: value }))}
                    placeholder="Select Message Type"
                    clearable={false}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <SearchableDropdown
                    options={[
                      { value: 'LOW', label: 'Low' },
                      { value: 'NORMAL', label: 'Normal' },
                      { value: 'HIGH', label: 'High' },
                      { value: 'URGENT', label: 'Urgent' }
                    ]}
                    value={formData.priority}
                    onChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                    placeholder="Select Priority"
                    clearable={false}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Validity Period (minutes)
                  </label>
                  <input
                    type="number"
                    name="validity_period"
                    value={formData.validity_period}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schedule Time (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    name="schedule_time"
                    value={formData.schedule_time}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="flash_sms"
                    checked={formData.flash_sms}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Flash SMS</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="unicode"
                    checked={formData.unicode}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Unicode</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="long_sms"
                    checked={formData.long_sms}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Long SMS</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="dlt_required"
                    checked={formData.dlt_required}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">DLT Required</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="delivery_report"
                    checked={formData.delivery_report}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Delivery Report</span>
                </label>
              </div>
            </div>

            {/* DLT Information */}
            {formData.dlt_required && (
              <div className="bg-red-50 p-4 rounded-lg">
                <h2 className="text-xl font-semibold text-red-800 mb-4">🔐 DLT Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DLT Entity ID
                    </label>
                    <input
                      type="text"
                      name="dlt_entity_id"
                      value={formData.dlt_entity_id}
                      onChange={handleInputChange}
                      placeholder="1234567890123456789"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      DLT Template ID
                    </label>
                    <input
                      type="text"
                      name="dlt_template_id"
                      value={formData.dlt_template_id}
                      onChange={handleInputChange}
                      placeholder="1234567890123456789"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Advanced Options */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">🔧 Advanced Options</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Parameters (JSON)
                </label>
                <textarea
                  name="custom_params"
                  value={formData.custom_params}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder='{"key1": "value1", "key2": "value2"}'
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Enter valid JSON for custom parameters</p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="submit"
                disabled={loading}
                className={`px-8 py-3 rounded-lg font-semibold text-white transition ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {loading ? '📤 Sending...' : '📤 Send SMS'}
              </button>
            </div>
          </form>

          {/* Result Display */}
          {result && (
            <div className={`mt-6 p-4 rounded-lg ${result.success ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500'} border-2`}>
              <h3 className={`text-lg font-semibold mb-2 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                {result.success ? '✅ Success' : '❌ Error'}
              </h3>
              <p className={`mb-2 ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                {result.message}
              </p>
              {result.success && result.data && (
                <div className="bg-white p-3 rounded mt-2">
                  <pre className="text-sm overflow-auto">
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                </div>
              )}
              {!result.success && result.error && (
                <div className="bg-white p-3 rounded mt-2">
                  <p className="text-sm text-red-600 font-semibold">{result.error}</p>
                  {result.details && (
                    <p className="text-sm text-red-500 mt-2">Details: {result.details}</p>
                  )}
                  {result.debug && (
                    <pre className="text-xs text-gray-700 mt-2 overflow-auto">
                      {JSON.stringify(result.debug, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestSMS;
